export type Language = "th" | "en";
export type Page = "home" | "team" | "careers" | "contact";

export type Leader = {
  role: string;
  name: string;
  image: string;
  paragraphs: string[];
};

export const company = {
  email: "contact@poweramc.com",
  careersEmail: "careers@poweramc.com",
  phone: "+66 (0) 2-124-5242",
  line: "@powerasset",
  lineUrl: "https://lin.ee/UeWdVjC",
  facebook: "https://www.facebook.com/share/1CcWn5m1hw/?mibextid=wwXIfr",
  linkedin:
    "https://www.linkedin.com/company/power-asset-management-company-limited-pam/",
  addressEn:
    "Skyy 9 Centre 554/95, 18th Floor, Unit 06 Asoke-Din Daeng Road, Din Daeng, Bangkok 10400",
  addressTh:
    "Skyy 9 Centre เลขที่ 554/95 ชั้น 18 Unit 06 ถนนอโศก–ดินแดง แขวงดินแดง เขตดินแดง กรุงเทพฯ 10400",
};

export const pathFor = (language: Language, page: Page) => {
  if (language === "en") {
    return page === "home" ? "/en" : `/en/${page}`;
  }

  if (page === "home") return "/";
  return page === "team" ? "/team-th" : `/${page}-th`;
};

export const pageFromPath = (pathname: string): Page => {
  if (pathname.includes("team")) return "team";
  if (pathname.includes("careers")) return "careers";
  if (pathname.includes("contact")) return "contact";
  return "home";
};

export const languageFromPath = (pathname: string): Language =>
  pathname === "/en" || pathname.startsWith("/en/") ? "en" : "th";

export const copy = {
  en: {
    nav: {
      home: "About Us",
      team: "Team",
      careers: "Careers",
      contact: "Contact",
    },
    hero: {
      eyebrow: "POWER ASSET MANAGEMENT",
      title: "Empowering financial transformation for a more sustainable future",
      cta: "Our services",
      scroll: "Discover PAM",
    },
    about: {
      label: "About us",
      title: "Financial recovery,\nwith people at its center.",
      intro:
        "At Power Asset Management Co., Ltd. (PAM), we deliver customer-centric solutions in Non-Performing Loan investment and asset management.",
      paragraphs: [
        "Licensed by the Bank of Thailand under the Royal Decree on Asset Management Companies B.E. 2541, we acquire and manage distressed assets from financial institutions through negotiation, restructuring, and structured legal enforcement.",
        "We believe debt resolution should restore stability and create new opportunities—for customers seeking fair outcomes and institutions building healthier portfolios.",
      ],
    },
    business: {
      label: "What we do",
      title: "Our core business verticals",
      intro:
        "Data-led asset management and human-first resolution strategies, designed for sustainable outcomes.",
      services: [
        {
          number: "01",
          title: "NPL investment & management",
          text: "We acquire secured and unsecured non-performing loans and work closely with customers to build practical, personalized repayment solutions.",
          points: [
            "Debt acquisition and portfolio management",
            "Customer-centric debt collection",
            "Personalized debt restructuring",
            "Legal and asset enforcement",
          ],
          image:
            "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=1400&q=85",
        },
        {
          number: "02",
          title: "Debt resolution & customer assistance",
          text: "We go beyond collections. Our teams provide guidance, flexible plans, and respectful communication to help customers regain financial stability.",
          points: [
            "Financial counseling and negotiation",
            "Flexible repayment and settlement plans",
            "Fair, transparent, and ethical practices",
          ],
          image:
            "https://images.unsplash.com/photo-1526958097901-5e6d742d3371?auto=format&fit=crop&w=1400&q=85",
        },
        {
          number: "03",
          title: "NPA & collateral management",
          text: "We manage foreclosed properties and collateral assets to maximize value for buyers and sellers through a clear end-to-end process.",
          points: [
            "Fair market valuation and resale",
            "Asset enhancement and investment",
            "Seamless transaction support",
          ],
          image:
            "https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&w=1400&q=85",
        },
      ],
    },
    why: {
      label: "Why PAM",
      title: "Built for fair outcomes and lasting value.",
      points: [
        [
          "Customer-centric",
          "Solutions shaped around dignity, fairness, and long-term stability.",
        ],
        [
          "Licensed & regulated",
          "Approved by the Bank of Thailand for NPL investment and management.",
        ],
        [
          "Flexible & personal",
          "Restructuring and repayment plans aligned with real circumstances.",
        ],
        [
          "Transparent & ethical",
          "Integrity and professional standards in every interaction.",
        ],
        [
          "Economic & social impact",
          "Helping people, businesses, and institutions recover and thrive.",
        ],
      ],
    },
    purpose: {
      label: "Our purpose",
      items: [
        {
          title: "Mission",
          text: "To deliver ethical, customer-focused solutions in distressed asset management—empowering individuals, businesses, and financial institutions to recover, rebuild, and grow.",
        },
        {
          title: "Vision",
          text: "To become Thailand’s most trusted and innovative asset management company, transforming financial challenges into sustainable opportunities.",
        },
        {
          title: "Culture",
          text: "Empathy, resilience, and responsibility guide a people-first culture where every voice is heard and every solution is designed to uplift.",
        },
      ],
    },
    values: {
      label: "Our core values",
      title: "HEARTS",
      intro: "Six principles guide how we think, work, and lead.",
      items: [
        ["H", "Honesty", "Transparency, integrity, and fairness."],
        ["E", "Empathy", "Understanding, compassion, and respect."],
        ["A", "Accountability", "Ownership of our actions and promises."],
        ["R", "Resilience", "Strength, adaptability, and a solution mindset."],
        ["T", "Teamwork", "Collaboration, support, and shared growth."],
        ["S", "Sustainability", "Long-term value and responsible progress."],
      ],
    },
    team: {
      label: "Our people",
      title: "Our Team",
      introTitle: "At PAM",
      intro:
        "We combine industry expertise, innovative technology, and ethical practices to transform Thailand’s financial recovery landscape. Our team brings decades of global experience in asset management, collections, banking, law, and financial technology.",
    },
    careers: {
      label: "Careers",
      title: "Join us in shaping a more resilient financial future.",
      intro:
        "We are more than an asset management company. We are a purpose-driven team redefining financial recovery and well-being across Thailand.",
      whyTitle: "Why work with us?",
      reasons: [
        [
          "Make meaningful impact",
          "Help people, families, and businesses regain their financial footing through sustainable, human-first strategies.",
        ],
        [
          "Grow with purpose",
          "Build your skills in an environment that values learning, innovation, and continuous development.",
        ],
        [
          "Be part of something bigger",
          "Contribute to a more inclusive and resilient economy through ethical asset management.",
        ],
        [
          "Innovate for good",
          "Use technology and data to drive smarter decisions and better outcomes for every stakeholder.",
        ],
      ],
      cultureTitle: "Our people culture",
      culture:
        "We foster a collaborative, inclusive, and respectful workplace. We encourage creativity, value accountability and empathy, and celebrate diverse perspectives.",
      openingsTitle: "We’re looking for",
      openingsIntro:
        "Whether you are a seasoned finance professional, a legal expert, a technology innovator, or someone who cares deeply about helping others, we would love to hear from you.",
      roles: [
        "Debt recovery & restructuring specialists",
        "Financial analysts & portfolio managers",
        "Legal & compliance experts",
        "Real estate & asset management professionals",
        "Customer engagement & counseling officers",
        "Technology & data innovation enthusiasts",
      ],
      ctaTitle: "Come build the future with us.",
      cta:
        "Don’t see a role that fits? Send us your resume. We are always happy to connect with passionate, driven people.",
      ctaButton: "Email your resume",
    },
    contact: {
      label: "Contact",
      title: "Let’s find the right path forward.",
      intro:
        "Reach out to Power Asset Management for assistance with non-performing loans and asset management solutions tailored to your needs.",
      location: "Location",
      email: "Email",
      phone: "Phone",
      line: "Official LINE",
      directions: "Open in maps",
    },
    footer: {
      statement:
        "Empowering financial transformation for a more sustainable future.",
      contact: "Contact us",
      address: "Company address",
      rights: "All rights reserved.",
    },
  },
  th: {
    nav: {
      home: "เกี่ยวกับเรา",
      team: "ทีมงาน",
      careers: "ตำแหน่งงาน",
      contact: "ติดต่อเรา",
    },
    hero: {
      eyebrow: "POWER ASSET MANAGEMENT",
      title: "ร่วมฟื้นฟูระบบการเงิน เพื่อสร้างอนาคตที่ยั่งยืน",
      cta: "บริการของเรา",
      scroll: "รู้จัก PAM",
    },
    about: {
      label: "เกี่ยวกับเรา",
      title: "ฟื้นฟูความมั่นคงทางการเงิน\nโดยมีมนุษย์เป็นศูนย์กลาง",
      intro:
        "บริษัท พาวเวอร์ แอสเซท แมเนจเมนท์ จำกัด (PAM) มุ่งมั่นส่งมอบโซลูชันด้านการลงทุนในหนี้ด้อยคุณภาพและการบริหารสินทรัพย์ที่ยึดลูกค้าเป็นศูนย์กลาง",
      paragraphs: [
        "เราเป็นบริษัทบริหารสินทรัพย์แบบครบวงจรที่ได้รับใบอนุญาตจากธนาคารแห่งประเทศไทย ภายใต้พระราชกำหนดบริษัทบริหารสินทรัพย์ พ.ศ. 2541 เชี่ยวชาญในการเข้าซื้อและบริหารสินทรัพย์ด้อยคุณภาพจากสถาบันการเงิน",
        "เราเชื่อว่าการบริหารหนี้ไม่ควรเป็นเพียงการเรียกเก็บ แต่ควรเป็นจุดเริ่มต้นของความมั่นคงทางการเงินและการสร้างโอกาสใหม่ให้กับทุกภาคส่วน",
      ],
    },
    business: {
      label: "สิ่งที่เราทำ",
      title: "เสาหลักธุรกิจของเรา",
      intro:
        "การบริหารสินทรัพย์ด้วยข้อมูล ผสานแนวทางการแก้ไขหนี้ที่เข้าใจผู้คน เพื่อผลลัพธ์ที่ยั่งยืน",
      services: [
        {
          number: "01",
          title: "การลงทุนและบริหารหนี้ด้อยคุณภาพ",
          text: "เราเข้าซื้อหนี้ด้อยคุณภาพทั้งที่มีและไม่มีหลักประกัน พร้อมทำงานกับลูกค้าอย่างใกล้ชิด เพื่อสร้างแผนชำระหนี้ที่เหมาะสมกับแต่ละราย",
          points: [
            "การเข้าซื้อหนี้และบริหารพอร์ต",
            "การติดตามหนี้ที่ยึดลูกค้าเป็นศูนย์กลาง",
            "การปรับโครงสร้างหนี้แบบเฉพาะราย",
            "การดำเนินการทางกฎหมายและบังคับคดี",
          ],
          image:
            "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?auto=format&fit=crop&w=1400&q=85",
        },
        {
          number: "02",
          title: "การแก้ไขหนี้และช่วยเหลือลูกค้า",
          text: "เราไม่ได้เพียงติดตามหนี้ แต่ร่วมเป็นพันธมิตรเพื่อช่วยให้ลูกค้ากลับคืนสู่ความมั่นคงทางการเงินอีกครั้ง",
          points: [
            "การให้คำปรึกษาทางการเงินและการเจรจา",
            "แผนชำระหนี้และการปิดบัญชีที่ยืดหยุ่น",
            "แนวปฏิบัติที่เป็นธรรมและมีจริยธรรม",
          ],
          image:
            "https://images.unsplash.com/photo-1526958097901-5e6d742d3371?auto=format&fit=crop&w=1400&q=85",
        },
        {
          number: "03",
          title: "การบริหารสินทรัพย์รอการขาย",
          text: "เราบริหารทรัพย์ที่ถูกยึดและหลักทรัพย์ค้ำประกัน โดยมุ่งเพิ่มมูลค่าให้ทั้งผู้ซื้อและผู้ขายผ่านกระบวนการที่โปร่งใส",
          points: [
            "การประเมินมูลค่าที่ยุติธรรมและการขายต่อ",
            "การปรับปรุงและลงทุนในทรัพย์สิน",
            "การสนับสนุนการโอนกรรมสิทธิ์อย่างราบรื่น",
          ],
          image:
            "https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&w=1400&q=85",
        },
      ],
    },
    why: {
      label: "ทำไมต้อง PAM",
      title: "สร้างขึ้นเพื่อทางออกที่เป็นธรรม และคุณค่าที่ยั่งยืน",
      points: [
        [
          "ยึดลูกค้าเป็นศูนย์กลาง",
          "มุ่งเน้นทางออกที่ให้เกียรติ เป็นธรรม และสร้างความมั่นคงระยะยาว",
        ],
        [
          "ได้รับใบอนุญาตและกำกับดูแล",
          "ได้รับอนุมัติจากธนาคารแห่งประเทศไทยในการลงทุนและบริหารหนี้ด้อยคุณภาพ",
        ],
        [
          "ยืดหยุ่นและเฉพาะราย",
          "ออกแบบแผนปรับโครงสร้างและชำระหนี้ให้สอดคล้องกับสถานการณ์จริง",
        ],
        [
          "โปร่งใสและมีจริยธรรม",
          "ยึดมั่นในความซื่อสัตย์และความเป็นมืออาชีพทุกขั้นตอน",
        ],
        [
          "สร้างผลกระทบเชิงบวก",
          "ช่วยให้บุคคล ธุรกิจ และสถาบันการเงินฟื้นตัวและเติบโตอย่างยั่งยืน",
        ],
      ],
    },
    purpose: {
      label: "เป้าหมายของเรา",
      items: [
        {
          title: "พันธกิจ",
          text: "ส่งมอบโซลูชันบริหารสินทรัพย์ที่มีปัญหาอย่างมีจริยธรรมและยึดลูกค้าเป็นศูนย์กลาง เพื่อให้ทุกภาคส่วนฟื้นฟู สร้างใหม่ และเติบโตได้อย่างมั่นคง",
        },
        {
          title: "วิสัยทัศน์",
          text: "เป็นบริษัทบริหารสินทรัพย์ที่น่าเชื่อถือและสร้างสรรค์ที่สุดในประเทศไทย เปลี่ยนความท้าทายทางการเงินให้กลายเป็นโอกาสที่ยั่งยืน",
        },
        {
          title: "วัฒนธรรม",
          text: "ความเข้าใจผู้อื่น ความมุ่งมั่น และความรับผิดชอบ หล่อหลอมวัฒนธรรมที่มนุษย์มาก่อน ทุกเสียงมีความหมาย และทุกทางออกช่วยยกระดับชีวิต",
        },
      ],
    },
    values: {
      label: "ค่านิยมหลักของเรา",
      title: "HEARTS",
      intro: "หกหลักการที่กำหนดวิธีคิด วิธีทำงาน และวิธีที่เรานำองค์กร",
      items: [
        ["H", "Honesty", "ซื่อสัตย์ โปร่งใส และยุติธรรม"],
        ["E", "Empathy", "เข้าใจ เห็นอกเห็นใจ และให้เกียรติ"],
        ["A", "Accountability", "รับผิดชอบต่อการกระทำและคำมั่นสัญญา"],
        ["R", "Resilience", "เข้มแข็ง ปรับตัว และมุ่งแก้ปัญหา"],
        ["T", "Teamwork", "ร่วมมือ สนับสนุน และเติบโตไปด้วยกัน"],
        ["S", "Sustainability", "สร้างคุณค่าระยะยาวอย่างรับผิดชอบ"],
      ],
    },
    team: {
      label: "คนของเรา",
      title: "ทีม PAM",
      introTitle: "ที่ PAM",
      intro:
        "เราผสานความเชี่ยวชาญในอุตสาหกรรม เทคโนโลยีนวัตกรรม และแนวทางการทำงานที่มีจริยธรรม เพื่อเปลี่ยนโฉมภูมิทัศน์การฟื้นฟูทางการเงินในประเทศไทย ทีมของเรามีประสบการณ์ระดับโลกในด้านการบริหารสินทรัพย์ การติดตามหนี้ และเทคโนโลยีทางการเงิน",
    },
    careers: {
      label: "ตำแหน่งงาน",
      title: "ร่วมสร้างอนาคตทางการเงินที่แข็งแกร่งและยั่งยืนไปกับเรา",
      intro:
        "เราไม่ได้เป็นเพียงบริษัทบริหารสินทรัพย์ แต่คือทีมที่ขับเคลื่อนด้วยเป้าหมายและความตั้งใจ ในการให้คำนิยามใหม่ของการฟื้นฟูหนี้และสร้างความเป็นอยู่ทางการเงินที่มั่นคงให้ประเทศไทย",
      whyTitle: "ทำไมต้อง PAM",
      reasons: [
        [
          "สร้างผลลัพธ์ที่มีความหมาย",
          "ช่วยเหลือบุคคล ครอบครัว และธุรกิจให้กลับมายืนได้อย่างมั่นคงด้วยแนวทางที่เข้าใจมนุษย์",
        ],
        [
          "เติบโตอย่างมีเป้าหมาย",
          "ทำงานในสภาพแวดล้อมที่ให้ความสำคัญกับการเรียนรู้ นวัตกรรม และการพัฒนาตนเอง",
        ],
        [
          "เป็นส่วนหนึ่งของสิ่งที่ยิ่งใหญ่",
          "ร่วมสร้างระบบเศรษฐกิจที่ครอบคลุมและยืดหยุ่น ผ่านการบริหารสินทรัพย์อย่างมีจริยธรรม",
        ],
        [
          "นวัตกรรมเพื่อประโยชน์ส่วนรวม",
          "ใช้เทคโนโลยีและข้อมูลในการตัดสินใจอย่างชาญฉลาด เพื่อผลลัพธ์ที่ดีกว่าสำหรับทุกฝ่าย",
        ],
      ],
      cultureTitle: "วัฒนธรรมการทำงานของเรา",
      culture:
        "เราส่งเสริมการทำงานร่วมกันอย่างเปิดกว้างและเคารพซึ่งกันและกัน กระตุ้นความคิดสร้างสรรค์ ยึดมั่นในความซื่อสัตย์ ความรับผิดชอบ และเห็นคุณค่าของมุมมองที่หลากหลาย",
      openingsTitle: "เรากำลังมองหา",
      openingsIntro:
        "ไม่ว่าคุณจะเป็นผู้เชี่ยวชาญด้านการเงิน ผู้มีความรู้กฎหมาย นักนวัตกรรมสายเทคโนโลยี หรือคนที่ใส่ใจอยากช่วยเหลือผู้อื่นจากใจจริง เรายินดีเสมอที่จะได้รู้จักคุณ",
      roles: [
        "ผู้เชี่ยวชาญด้านการติดตามและปรับโครงสร้างหนี้",
        "นักวิเคราะห์การเงินและผู้จัดการพอร์ต",
        "ผู้เชี่ยวชาญด้านกฎหมายและการกำกับดูแล",
        "ผู้เชี่ยวชาญด้านอสังหาริมทรัพย์และการบริหารสินทรัพย์",
        "เจ้าหน้าที่ดูแลลูกค้าและให้คำปรึกษาทางการเงิน",
        "ผู้ที่มีใจรักนวัตกรรม เทคโนโลยี และข้อมูล",
      ],
      ctaTitle: "มาร่วมสร้างอนาคตไปด้วยกัน",
      cta:
        "ยังไม่เจอตำแหน่งที่ใช่? ส่งเรซูเม่มาให้เรา เรายินดีเสมอที่จะได้รู้จักผู้ที่มีใจรักในการทำงานและพร้อมเติบโตไปด้วยกัน",
      ctaButton: "ส่งเรซูเม่ทางอีเมล",
    },
    contact: {
      label: "ติดต่อเรา",
      title: "ร่วมกันค้นหาทางออกที่เหมาะสม",
      intro:
        "ติดต่อ Power Asset Management เพื่อรับคำปรึกษาเกี่ยวกับหนี้ด้อยคุณภาพและโซลูชันการบริหารสินทรัพย์ที่ออกแบบให้เหมาะกับความต้องการของคุณ",
      location: "ที่อยู่บริษัท",
      email: "อีเมล",
      phone: "โทรศัพท์",
      line: "LINE Official Account",
      directions: "เปิดในแผนที่",
    },
    footer: {
      statement: "ร่วมฟื้นฟูระบบการเงิน เพื่อสร้างอนาคตที่ยั่งยืน",
      contact: "ติดต่อเรา",
      address: "ที่อยู่บริษัท",
      rights: "สงวนลิขสิทธิ์",
    },
  },
} as const;

export const teamEn: Leader[] = [
  {
    role: "Founder / Director",
    name: "Mr. Kautilya Tushar Rajyaguru",
    image: "/assets/founder.jpg",
    paragraphs: [
      "A young and dynamic entrepreneur, Kautilya carries a legacy of entrepreneurship from his family’s diversified global conglomerate spanning India, the Middle East, and Africa. Leadership roles from finance to infrastructure shaped his expertise in strategic growth, innovation, and sustainable business.",
      "He holds a Bachelor’s degree in International Business Economics from Budapest Metropolitan University and is focused on building transformative, technology-led ventures that create long-term value.",
    ],
  },
  {
    role: "Director",
    name: "Mrs. Theeradaporn Phuwaprapaboon",
    image: "/assets/director.jpg",
    paragraphs: [
      "A seasoned leader with more than 15 years of expertise in debt collection and human resource management, Theeradaporn has driven operational excellence within leading banks and asset management companies in Thailand.",
      "Her experience spans collection optimization, workforce strategy, regulatory compliance, and the development of high-performing teams.",
    ],
  },
  {
    role: "Managing Director",
    name: "Miss Raewadee Katekaew",
    image: "/assets/managing-director.jpg",
    paragraphs: [
      "With more than 25 years in banking and financial services, Raewadee specializes in asset management, financial restructuring, and strategic growth. She has held senior roles at Siam Commercial Bank, Bank of Ayudhya, CIMB Thai Bank, and KASIKORNBANK.",
      "Her expertise spans NPL management, financial innovation, banking transformation, and risk compliance.",
    ],
  },
  {
    role: "CEO · Chief Executive Officer",
    name: "Mr. Deepak Jayaram Shetty",
    image: "/assets/ceo.jpg",
    paragraphs: [
      "Deepak brings more than 24 years of expertise in credit, collections, and NPL management across Asia, Australia, Europe, the United Kingdom, and the United States.",
      "His leadership experience includes HDFC Bank, Citi Bank India, Repcol Australia, Global Vantedge, GE Capital, and Aiqon Capital Group. He holds an MBA from Bangalore University.",
    ],
  },
  {
    role: "CTO · Chief Technology Officer",
    name: "Mr. Pichit Chitnelawong",
    image: "/assets/cto.jpg",
    paragraphs: [
      "Pichit is a senior technology leader with more than two decades of experience delivering complex digital transformation programs across banking, insurance, logistics, trading, and telecommunications.",
      "He brings deep expertise in technology strategy, solution architecture, systems integration, program delivery, and cross-functional leadership.",
    ],
  },
  {
    role: "CGO · Chief Growth Officer",
    name: "Miss Natchanan Dechaakhrawanit",
    image: "/assets/cgo.jpg",
    paragraphs: [
      "A strategic growth leader with more than 20 years in corporate management, executive recruitment, and business expansion, Natchanan excels in partnerships, market development, and organizational scale.",
      "She holds an MSc in Financial Investment and Risk Management from NIDA and a BBA from Prince of Songkla University.",
    ],
  },
  {
    role: "CPO · Chief People Officer",
    name: "Ms. Pattaraporn Juengtaweesut",
    image: "/assets/cpo.jpg",
    paragraphs: [
      "With more than 20 years in human resource management, Pattaraporn is known for driving organizational growth and transformation across leading companies in Thailand.",
      "Her people-first leadership centers on talent, culture, engagement, integrity, and sustainable workforce development.",
    ],
  },
  {
    role: "CAO · Chief Accounting Officer",
    name: "Mr. Songkran Limsuk",
    image: "/assets/cao.jpg",
    paragraphs: [
      "A seasoned financial leader with deep expertise in accounting, compliance, and strategic financial planning, Songkran oversees reporting, regulatory adherence, and internal controls.",
      "His disciplined, forward-looking approach aligns financial operations with the company’s growth and sustainability goals.",
    ],
  },
  {
    role: "CLO · Chief Legal Officer",
    name: "Mr. Putthinan Plangkull",
    image: "/assets/clo.jpg",
    paragraphs: [
      "Putthinan is a seasoned lawyer with more than 30 years of practice in corporate law, insurance, and debt collection. He has advised corporations, financial institutions, and insurers in Thailand and internationally.",
      "His credentials include the Director Certification Program from the Thai Institute of Directors, complementing his experience in governance, transactions, dispute resolution, and large-scale recoveries.",
    ],
  },
  {
    role: "Head of Collection",
    name: "Ms. Thatchaporn Yimtim",
    image: "/assets/collection.jpg",
    paragraphs: [
      "Thatchaporn brings extensive expertise in debt recovery and credit management, leading end-to-end strategies across secured and unsecured portfolios.",
      "Her hands-on, customer-centric approach builds effective collection frameworks while maintaining high ethical and regulatory standards.",
    ],
  },
];

export const teamTh: Leader[] = [
  {
    role: "Founder / Director",
    name: "Mr. Kautilya Tushar Rajyaguru",
    image: "/assets/founder.jpg",
    paragraphs: [
      "ผู้ประกอบการรุ่นใหม่มากวิสัยทัศน์ สืบทอดสายเลือดนักธุรกิจระดับโลกจากกลุ่มธุรกิจของครอบครัวที่ครอบคลุมอินเดีย ตะวันออกกลาง และแอฟริกา สั่งสมประสบการณ์ด้านการเติบโตเชิงกลยุทธ์ นวัตกรรม และธุรกิจที่ยั่งยืน",
      "สำเร็จการศึกษาด้านเศรษฐศาสตร์ธุรกิจระหว่างประเทศจาก Budapest Metropolitan University และมุ่งสร้างธุรกิจที่ขับเคลื่อนด้วยเทคโนโลยีและสร้างคุณค่าระยะยาว",
    ],
  },
  {
    role: "Director",
    name: "Mrs. Theeradaporn Phuwaprapaboon",
    image: "/assets/director.jpg",
    paragraphs: [
      "ผู้นำมากประสบการณ์กว่า 15 ปีด้านการติดตามหนี้และการบริหารทรัพยากรบุคคล มีบทบาทสำคัญในการขับเคลื่อนความเป็นเลิศด้านการดำเนินงานให้กับธนาคารและบริษัทบริหารสินทรัพย์ชั้นนำในประเทศไทย",
      "เชี่ยวชาญการปรับปรุงกระบวนการติดตามหนี้ การบริหารทีมขนาดใหญ่ กลยุทธ์บุคลากร และการปฏิบัติตามกฎระเบียบ",
    ],
  },
  {
    role: "CEO · Chief Executive Officer",
    name: "Mr. Deepak Jayaram Shetty",
    image: "/assets/ceo.jpg",
    paragraphs: [
      "ผู้บริหารที่มีประสบการณ์กว่า 24 ปีด้านสินเชื่อ การติดตามหนี้ และการบริหารหนี้ด้อยคุณภาพ ครอบคลุมเอเชีย ออสเตรเลีย ยุโรป สหราชอาณาจักร และสหรัฐอเมริกา",
      "เคยดำรงตำแหน่งผู้นำใน HDFC Bank, Citi Bank India, Repcol Australia, Global Vantedge, GE Capital และ Aiqon Capital Group และสำเร็จ MBA จาก Bangalore University",
    ],
  },
  {
    role: "CGO · Chief Growth Officer",
    name: "Miss Natchanan Dechaakhrawanit",
    image: "/assets/cgo.jpg",
    paragraphs: [
      "ผู้นำเชิงกลยุทธ์ด้านการเติบโตของธุรกิจที่มีประสบการณ์กว่า 20 ปี เชี่ยวชาญด้านการบริหารองค์กร การสรรหาผู้บริหาร การสร้างพันธมิตร และการขยายตลาด",
      "สำเร็จปริญญาโทด้านการลงทุนและการบริหารความเสี่ยงทางการเงินจาก NIDA และปริญญาตรีบริหารธุรกิจจากมหาวิทยาลัยสงขลานครินทร์",
    ],
  },
  {
    role: "CPO · Chief People Officer",
    name: "Ms. Pattaraporn Juengtaweesut",
    image: "/assets/cpo.jpg",
    paragraphs: [
      "ผู้นำด้านทรัพยากรบุคคลที่มีประสบการณ์มากกว่า 20 ปี เชี่ยวชาญการบริหารและพัฒนาบุคลากร การพัฒนาภาวะผู้นำ และการวางกลยุทธ์ HR ให้สอดคล้องกับธุรกิจ",
      "ยึดมั่นในความโปร่งใส ความซื่อสัตย์ และการให้ความสำคัญกับคนเป็นศูนย์กลาง เพื่อสร้างทีมที่มีประสิทธิภาพสูงและวัฒนธรรมที่เปิดกว้าง",
    ],
  },
  {
    role: "CAO · Chief Accounting Officer",
    name: "Mr. Songkran Limsuk",
    image: "/assets/cao.jpg",
    paragraphs: [
      "ผู้นำด้านการเงินที่เชี่ยวชาญด้านบัญชี การกำกับดูแล และการวางแผนการเงินเชิงกลยุทธ์ ดูแลการรายงานทางการเงิน ข้อกำหนดของหน่วยงานกำกับ และระบบควบคุมภายใน",
      "มีบทบาทสำคัญในการสร้างความโปร่งใสและวางแนวทางด้านการเงินให้สอดคล้องกับเป้าหมายการเติบโตอย่างยั่งยืน",
    ],
  },
  {
    role: "CLO · Chief Legal Officer",
    name: "Mr. Putthinan Plangkull",
    image: "/assets/clo.jpg",
    paragraphs: [
      "ทนายความผู้มีประสบการณ์กว่า 30 ปี เชี่ยวชาญกฎหมายองค์กร ประกันภัย และการติดตามหนี้ ให้คำปรึกษาแก่บริษัท สถาบันการเงิน และบริษัทประกันภัยทั้งในและต่างประเทศ",
      "ได้รับการยอมรับด้านคำปรึกษาเชิงกลยุทธ์และธรรมาภิบาล รวมถึงวุฒิบัตร Director Certification Program จากสถาบันกรรมการไทย",
    ],
  },
  {
    role: "Head of Collection",
    name: "Ms. Thatchaporn Yimtim",
    image: "/assets/collection.jpg",
    paragraphs: [
      "หัวหน้าฝ่ายบริหารหนี้ที่มีความเชี่ยวชาญด้านการฟื้นฟูหนี้และการจัดการสินเชื่อ ดูแลกลยุทธ์การติดตามหนี้แบบครบวงจรสำหรับพอร์ตที่มีและไม่มีหลักประกัน",
      "เป็นที่ยอมรับจากภาวะผู้นำและแนวทางการทำงานที่คำนึงถึงลูกค้า พร้อมรักษามาตรฐานจริยธรรมและกฎระเบียบอย่างเคร่งครัด",
    ],
  },
];

